import { useGetCertificatesQuery } from "../../../common/api/certificate";
import {
    useGetEmployeeExpiredCertificatesQuery,
    useGetEstablishmentExpiredCertificatesQuery,
} from "../../../common/api/expiredCertificate";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useIsEstablishmentManager } from "../../../common/utils/useIsEstablishmentManager";
import { useEmployeeId } from "../../../common/utils/useEmployeeId";
import Skeleton from "react-loading-skeleton";
import {
    EmptyListNotice,
    Table,
    Tabs,
} from "@hatch-digital-nl/abs-storybook/legacy";
import { getTableColumns } from "../utils/getTableColumns";
import { getTableRows } from "../utils/getTableRows";
import { getExpiredTableColumns } from "../utils/getExpiredTableColumns";
import { getExpiredTableRows } from "../utils/getExpiredTableRows";
import { tabItems } from "../utils/tabItems";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useNavigate } from "react-router";
import ellipsisImage from "../../../common/images/ellipsis.svg";
import ellipsisWhiteImage from "../../../common/images/ellipsis-white.svg";
import { setCurrentPage, setItemsPerPage } from "../CertificateExpirySlice";
import React, { JSX, useEffect } from "react";

export const CertificateExpiryContent = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isEstablishmentManager = useIsEstablishmentManager();
    const establishmentId = useEstablishmentId();
    const employeeId = useEmployeeId();
    const { activeTab, currentPage, itemsPerPage, itemsPerPageSelect, sort } =
        useAppSelector((state) => state.certificateExpiry);
    const { isPostCreateCertificateSuccess } = useAppSelector(
        (state) => state.createCertificateModal,
    );

    const isExpiringSoonTab = activeTab === "expiring_soon";
    const isExpiredTab = activeTab === "expired";

    const {
        data: certificatesData,
        isLoading: certificatesDataIsLoading,
        refetch: refetchCertificates,
    } = useGetCertificatesQuery(
        {
            page: currentPage,
            items_per_page: itemsPerPage,
            sort: sort.sortName,
            filter: {
                expiring_soon: true,
                establishment_id: establishmentId,
                ...(!isEstablishmentManager ? { employee_id: employeeId } : {}),
            },
        },
        { skip: !isExpiringSoonTab },
    );

    const {
        data: employeeExpiredData,
        isLoading: employeeExpiredIsLoading,
        refetch: refetchEmployeeExpired,
    } = useGetEmployeeExpiredCertificatesQuery(
        {
            page: currentPage,
            items_per_page: itemsPerPage,
        },
        { skip: isEstablishmentManager || !isExpiredTab },
    );

    const {
        data: establishmentExpiredData,
        isLoading: establishmentExpiredIsLoading,
        refetch: refetchEstablishmentExpired,
    } = useGetEstablishmentExpiredCertificatesQuery(
        {
            establishmentId,
            page: currentPage,
            items_per_page: itemsPerPage,
        },
        {
            skip: !isEstablishmentManager || !establishmentId || !isExpiredTab,
        },
    );

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            return;
        }

        if (isExpiringSoonTab) {
            refetchCertificates();
            return;
        }

        if (isEstablishmentManager) {
            refetchEstablishmentExpired();
            return;
        }

        refetchEmployeeExpired();
    }, [
        refetchCertificates,
        refetchEmployeeExpired,
        refetchEstablishmentExpired,
        isPostCreateCertificateSuccess,
        isExpiringSoonTab,
        isEstablishmentManager,
    ]);

    const expiredData = isEstablishmentManager
        ? establishmentExpiredData
        : employeeExpiredData;
    const expiredIsLoading = isEstablishmentManager
        ? establishmentExpiredIsLoading
        : employeeExpiredIsLoading;

    const activeIsLoading = isExpiringSoonTab
        ? certificatesDataIsLoading
        : expiredIsLoading;

    const renderBody = (): JSX.Element => {
        if (activeIsLoading) {
            return <Skeleton height={"60rem"} borderRadius={"1.5rem"} />;
        }

        if (isExpiringSoonTab) {
            if (
                !certificatesData ||
                !certificatesData.data ||
                !certificatesData.meta ||
                !certificatesData.data.length
            ) {
                return (
                    <EmptyListNotice
                        content={
                            "Er zijn geen certificaten die binnenkort verlopen"
                        }
                    />
                );
            }

            return (
                <Table
                    data={{
                        columns: getTableColumns({
                            isEstablishmentManager,
                            dispatch,
                            sort,
                        }),
                        rows: getTableRows({
                            certificates: certificatesData.data,
                            dispatch,
                            navigate,
                            isEstablishmentManager,
                        }),
                        total: certificatesData.meta.total,
                    }}
                    iconButtonIconUrl={ellipsisImage}
                    iconButtonBackgroundColor={"#F3F4F6"}
                    onOpenIconButtonUrl={ellipsisWhiteImage}
                    onOpenIconButtonBackgroundColor={"#1D2D58"}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    itemsPerPageSelect={itemsPerPageSelect}
                    onItemsPerPageChange={(
                        event: React.ChangeEvent<HTMLSelectElement>,
                    ) => dispatch(setItemsPerPage(Number(event.target.value)))}
                    onPaginationChange={(page) =>
                        dispatch(setCurrentPage(page))
                    }
                    isPagination={true}
                />
            );
        }

        if (
            !expiredData ||
            !expiredData.data ||
            !expiredData.meta ||
            !expiredData.data.length
        ) {
            return (
                <EmptyListNotice
                    content={"Er zijn geen verlopen certificaten"}
                />
            );
        }

        return (
            <Table
                data={{
                    columns: getExpiredTableColumns({
                        isEstablishmentManager,
                    }),
                    rows: getExpiredTableRows({
                        certificates: expiredData.data,
                        dispatch,
                        navigate,
                        isEstablishmentManager,
                    }),
                    total: expiredData.meta.total,
                }}
                iconButtonIconUrl={ellipsisImage}
                iconButtonBackgroundColor={"#F3F4F6"}
                onOpenIconButtonUrl={ellipsisWhiteImage}
                onOpenIconButtonBackgroundColor={"#1D2D58"}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                itemsPerPageSelect={itemsPerPageSelect}
                onItemsPerPageChange={(
                    event: React.ChangeEvent<HTMLSelectElement>,
                ) => dispatch(setItemsPerPage(Number(event.target.value)))}
                onPaginationChange={(page) => dispatch(setCurrentPage(page))}
                isPagination={true}
            />
        );
    };

    return (
        <div className="p-certificate-expiry__content">
            <Tabs items={tabItems({ dispatch, activeTab })} />
            {renderBody()}
        </div>
    );
};
